import { useEffect, useState } from 'react';
import { Modal } from '../ui/modal.jsx';
import { ConfirmDialog } from '../ui/confirm-dialog.jsx';
import { useToast } from '../ui/toast.jsx';
import { TypeBadge, PriorityBadge, ScopeBadge, HomeOnlyBadge } from '../channels/channel-badges.jsx';
import { ChannelSettingsFields, DEFAULT_CHANNEL_VALUES } from '../channels/channel-settings-fields.jsx';
import { MembersModal } from '../channels/members-modal.jsx';
import { api, ApiError } from '../../api/index.js';
import { formatUtcInTimezone } from '../../lib/time.js';

/**
 * Inline panel inside unit-detail that lists every OSS channel hosted
 * by this unit (2026-05 — multiple per unit, disambiguated by slug)
 * with per-row edit / delete / members. The header carries a "Create
 * channel" CTA that opens a modal where the operator picks an optional
 * slug (empty = unit primary channel, legacy 1-per-unit path).
 */
export function UnitChannelSection({ unit, viewerTimezone, isAdmin, onChanged }) {
  const toast = useToast();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget]     = useState(null);   // channel | null
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [membersTarget, setMembersTarget] = useState(null);

  const refresh = async () => {
    if (!unit) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.channels.byUnit(unit.id);
      // Server now returns an array under {channels: []}. Backwards
      // compat: a few stale responses may still ship {channel}; tolerate.
      const list = Array.isArray(r.channels)
        ? r.channels
        : (r.channel ? [r.channel] : []);
      setChannels(list);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [unit?.id]);

  async function handleCreate(payload) {
    await api.channels.createForUnit(unit.id, payload);
    toast.success('Channel created.');
    setCreateOpen(false);
    await refresh();
    onChanged?.();
  }

  async function handleSave(payload) {
    if (!editTarget) return;
    await api.channels.update(editTarget.id, payload);
    toast.success('Channel updated.');
    setEditTarget(null);
    await refresh();
    onChanged?.();
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await api.channels.remove(deleteTarget.id);
      toast.success('Channel deleted.');
      setDeleteTarget(null);
      await refresh();
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Delete failed.');
    }
  }

  return (
    <>
      <div className="flex items-center justify-between border-t border-line bg-canvas/40 px-6 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-mist">
          Channels {channels.length > 0 && (
            <span className="ml-1 normal-case font-normal text-mist/70">({channels.length})</span>
          )}
        </div>
        {isAdmin && !loading && (
          <button type="button" onClick={() => setCreateOpen(true)} className="btn-secondary text-xs">
            <i className="fa-solid fa-plus" aria-hidden="true" /> Create channel
          </button>
        )}
      </div>

      {loading && channels.length === 0 ? (
        <div className="px-6 py-4 text-center text-sm text-mist">
          <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden="true" /> Loading…
        </div>
      ) : error ? (
        <div className="px-6 py-4 text-sm text-rose-700">{error}</div>
      ) : channels.length === 0 ? (
        <div className="px-6 py-4 text-sm text-mist">
          No channels for this unit yet.{' '}
          <span className="text-[12px]">
            Click <em>Create channel</em> to derive one from the OSS hierarchy. Add an
            optional slug to host multiple OSS channels under the same unit.
          </span>
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {channels.map((ch) => (
            <li key={ch.id}>
              <ChannelInfo
                channel={ch}
                viewerTimezone={viewerTimezone}
                isAdmin={isAdmin}
                onMembers={() => setMembersTarget(ch)}
                onEdit={() => setEditTarget(ch)}
                onDelete={() => setDeleteTarget(ch)}
              />
            </li>
          ))}
        </ul>
      )}

      <CreateOssChannelModal
        open={createOpen}
        unit={unit}
        existingSlugs={channels.map((c) => c.slug)}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />

      <EditOssChannelModal
        open={!!editTarget}
        channel={editTarget}
        onClose={() => setEditTarget(null)}
        onSubmit={handleSave}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={`Delete channel "${deleteTarget?.name || ''}"?`}
        message={
          deleteTarget?.slug == null
            ? 'The unit primary channel will be removed. Personnel and the unit are unaffected.'
            : `Delete OSS channel slug="${deleteTarget?.slug}". Personnel and the unit are unaffected.`
        }
        confirmLabel="Delete"
        destructive
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />

      <MembersModal
        open={!!membersTarget}
        channel={membersTarget}
        onClose={() => setMembersTarget(null)}
      />
    </>
  );
}

function ChannelInfo({ channel, viewerTimezone, isAdmin, onMembers, onEdit, onDelete }) {
  return (
    <div className="px-6 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[12px] text-ink">{channel.path}</span>
        <TypeBadge type={channel.type} />
        <ScopeBadge scope={channel.scope} />
        <PriorityBadge priority={channel.priority} />
        <HomeOnlyBadge active={channel.homeNodeOnly} />
        {channel.archived && (
          <span className="rounded-full border border-line bg-canvas px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-mist">
            archived
          </span>
        )}
      </div>
      <div className="mt-1 text-sm font-semibold text-ink">{channel.name}</div>
      {channel.description && (
        <p className="mt-1 text-xs text-mist whitespace-pre-wrap">{channel.description}</p>
      )}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-[11px] text-mist">
        <div className="flex items-center gap-3">
          <span>
            <i className="fa-solid fa-users mr-1" aria-hidden="true" />
            {channel.memberCount} members
          </span>
          <span>
            <i className="fa-regular fa-clock mr-1" aria-hidden="true" />
            updated {formatUtcInTimezone(channel.updatedAt, viewerTimezone)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={onMembers} className="btn-ghost text-xs" title="Members">
            <i className="fa-solid fa-users" aria-hidden="true" /> Members
          </button>
          {isAdmin && (
            <>
              <button type="button" onClick={onEdit} className="btn-ghost text-xs" title="Edit">
                <i className="fa-solid fa-pen" aria-hidden="true" />
              </button>
              <button type="button" onClick={onDelete} className="btn-ghost text-xs text-rose-600 hover:bg-rose-soft" title="Delete">
                <i className="fa-solid fa-trash" aria-hidden="true" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Inline modals (shared shell, only the field-set differs from custom flow)
// ---------------------------------------------------------------------------

function CreateOssChannelModal({ open, unit, existingSlugs = [], onClose, onSubmit }) {
  const [values, setValues] = useState({ ...DEFAULT_CHANNEL_VALUES });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setValues({
      ...DEFAULT_CHANNEL_VALUES,
      // 2026-05 — multi-OSS-per-unit. If the unit already has a primary
      // channel (slug=null), pre-fill a sensible default slug so the
      // operator only has to tweak it; otherwise leave empty so the new
      // channel becomes the primary one.
      slug: existingSlugs.includes(null) ? '' : '',
      name: unit?.name ? `${unit.name} chat` : '',
    });
    setBusy(false);
    setError(null);
  }, [open, unit, existingSlugs.length]);

  // Slug rules: optional; if present, single segment lowercase a-z 0-9 - _.
  const slugRaw = (values.slug || '').trim().toLowerCase();
  const slugValid = slugRaw === '' || /^[a-z0-9_-]{1,64}$/.test(slugRaw);
  const slugClash = slugRaw === ''
    ? existingSlugs.includes(null)              // empty slug = primary; clash if primary already exists
    : existingSlugs.includes(slugRaw);          // duplicate explicit slug
  const slugError = !slugValid
    ? 'Slug must be 1-64 chars: lowercase a-z 0-9 - _'
    : slugClash
      ? (slugRaw === ''
          ? 'This unit already has a primary channel. Pick a slug to add another.'
          : `Slug "${slugRaw}" already exists for this unit.`)
      : null;

  const canSubmit = values.name.trim().length > 0 && !slugError && !busy;

  async function submit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        name: values.name.trim(),
        slug: slugRaw || null,
        type: values.type,
        scope: values.scope,
        priority: values.priority,
        retention: values.retention,
        description: values.description.trim() || null,
        homeNodeOnly: !!values.homeNodeOnly,
        // hot-fix #11
        dataTypes: Array.isArray(values.dataTypes) ? values.dataTypes : ['text'],
        // hot-fix #12
        chatRetentionDays: values.chatRetentionDays ?? null,
        // 2026-06-19 — PoC settings (orthogonal to chat/map).
        pocMode: values.pocMode || 'simplex',
        pocRecording: !!values.pocRecording,
        pocPttMaxSec: values.pocPttMaxSec ?? null,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message || 'Operation failed.');
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={`Create channel for "${unit?.name}"`}
      description="The channel path is auto-derived from the unit hierarchy and stays in sync with renames and moves."
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={busy} className="btn-secondary" data-skip-autofocus>
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!canSubmit} className="btn-primary">
            {busy ? (
              <>
                <span className="loader-spinner" aria-hidden="true" /> Creating…
              </>
            ) : (
              <>
                <i className="fa-solid fa-plus" aria-hidden="true" /> Create channel
              </>
            )}
          </button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700">
            <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
            {error}
          </div>
        )}
        <div>
          <label htmlFor="oss-ch-name" className="field-label">Display name</label>
          <input
            id="oss-ch-name"
            className="field"
            value={values.name}
            onChange={(e) => setValues((p) => ({ ...p, name: e.target.value }))}
            maxLength={128}
            disabled={busy}
            autoComplete="off"
          />
        </div>
        <ChannelSettingsFields
          values={values}
          onChange={setValues}
          errors={{ slug: slugError }}
          isOss
          busy={busy}
        />
      </form>
    </Modal>
  );
}

function EditOssChannelModal({ open, channel, onClose, onSubmit }) {
  const [values, setValues] = useState({ ...DEFAULT_CHANNEL_VALUES });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !channel) return;
    setValues({
      name: channel.name,
      type: channel.type,
      scope: channel.scope,
      priority: channel.priority,
      retention: { ...channel.retention },
      description: channel.description || '',
      archived: channel.archived,
      homeNodeOnly: !!channel.homeNodeOnly,
      dataTypes: Array.isArray(channel.dataTypes) && channel.dataTypes.length > 0
        ? channel.dataTypes.slice()
        : ['text'],
      chatRetentionDays: channel.chatRetentionDays ?? null,
      // 2026-06-19 — PoC settings (orthogonal to chat/map).
      pocMode: channel.pocMode || 'simplex',
      pocRecording: !!channel.pocRecording,
      pocPttMaxSec: channel.pocPttMaxSec ?? 180,
    });
    setBusy(false);
    setError(null);
  }, [open, channel]);

  const canSubmit = values.name.trim().length > 0 && !busy;

  async function submit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        name: values.name.trim(),
        type: values.type,
        scope: values.scope,
        priority: values.priority,
        retention: values.retention,
        description: values.description.trim() || null,
        archived: values.archived,
        homeNodeOnly: !!values.homeNodeOnly,
        // hot-fix #11
        dataTypes: Array.isArray(values.dataTypes) ? values.dataTypes : ['text'],
        // hot-fix #12
        chatRetentionDays: values.chatRetentionDays ?? null,
        // 2026-06-19 — PoC settings (orthogonal to chat/map).
        pocMode: values.pocMode || 'simplex',
        pocRecording: !!values.pocRecording,
        pocPttMaxSec: values.pocPttMaxSec ?? null,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message || 'Operation failed.');
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={`Edit channel — ${channel?.name || ''}`}
      description="Settings only. Membership comes from the OSS tree (move people there to change the roster)."
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={busy} className="btn-secondary" data-skip-autofocus>
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!canSubmit} className="btn-primary">
            {busy ? (
              <>
                <span className="loader-spinner" aria-hidden="true" /> Saving…
              </>
            ) : (
              <>
                <i className="fa-solid fa-floppy-disk" aria-hidden="true" /> Save changes
              </>
            )}
          </button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700">
            <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
            {error}
          </div>
        )}
        <div>
          <label htmlFor="ed-ch-name" className="field-label">Display name</label>
          <input
            id="ed-ch-name"
            className="field"
            value={values.name}
            onChange={(e) => setValues((p) => ({ ...p, name: e.target.value }))}
            maxLength={128}
            disabled={busy}
          />
        </div>
        {/* Hot-fix #10 follow-up: the Archived toggle now lives inside
            ChannelSettingsFields (gated by isEdit) so OSS-edit and
            custom-edit modals both expose it through the shared form
            without divergence. */}
        <ChannelSettingsFields values={values} onChange={setValues} isOss isEdit busy={busy} />
      </form>
    </Modal>
  );
}
